import { combineLatest, map, skip, tap } from 'rxjs';

import { AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';

import { ApmTranslatePipe, PermissionUtils } from '@mitsubishi/core';
import { DatasheetFacade } from '@mitsubishi/project/data-access';
import { IconComponent } from '@mitsubishi/shared/ui';

import { FieldWrapper, FormlyFieldConfig } from '@ngx-formly/core';

@Component({
  selector: 'apm-datasheet-section-wrapper',
  templateUrl: './datasheet-section-wrapper.component.html',
  styleUrls: ['./datasheet-section-wrapper.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IconComponent, AsyncPipe, ApmTranslatePipe, MatTooltip],
})
export class DatasheetSectionWrapperComponent extends FieldWrapper {
  #datasheetFacade = inject(DatasheetFacade);

  hasDatasheetMoreDetailsPermission = PermissionUtils.hasDatasheetMoreDetails();

  isEditing$ = this.#datasheetFacade.isEditing$.pipe(
    skip(1),
    tap(isEditing => {
      this.changeDisableOnAllFields(this.field, !isEditing);

      /* Trigger rebuild in order to re-run expressions */
      this.options.build?.();
    })
  );

  somethingEdited$ = this.#datasheetFacade.somethingEdited$;
  isSectionHidden$ = combineLatest([this.#datasheetFacade.selectedGroup$, this.#datasheetFacade.selectedSection$]).pipe(
    map(([groupId, sectionId]) => {
      return groupId !== this.field.props?.groupId || sectionId !== this.field.props?.sectionId;
    })
  );

  edit() {
    this.#datasheetFacade.setIsEditing(true);
  }

  calculate() {
    this.#datasheetFacade.setEditedValues(this.model);
    this.#datasheetFacade.setIsEditing(false);
    this.#datasheetFacade.loadDatasheet(true);
  }

  cancel() {
    this.#datasheetFacade.setEditedValues({});
    this.#datasheetFacade.setIsEditing(false);
    this.form.reset();
  }

  revert() {
    this.#datasheetFacade.setIsEditing(false);
    this.#datasheetFacade.loadDatasheet(false);
  }

  save() {
    this.#datasheetFacade.setIsEditing(false);
    this.#datasheetFacade.saveDatasheet();
  }

  changeDisableOnAllFields(field: FormlyFieldConfig, disabled: boolean) {
    if (field.props) {
      if (field.props.keepDisabled) {
        field.props.disabled = true;
        return;
      }
      field.props.disabled = disabled;
    }
    field.fieldGroup?.forEach(f => {
      this.changeDisableOnAllFields(f, disabled);
    });
  }

  downloadPartialLoads() {
    this.#datasheetFacade.downloadPartialLoads();
  }
}
